import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useFavoritos } from "@/hooks/useFavoritos";
import { useAuthState } from "@/hooks/useAuthState";
import { Heart, MapPin, Trash2, Users, Bed, Calendar } from "lucide-react";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";

import { getCoverImageUrl } from "@/lib/imageMap";

const Favoritos = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, user, userId, loading: authLoading } = useAuthState();
  const { favoritos, loading, removeFromFavoritos, clearFavoritos } = useFavoritos();

  // Aguardar carregamento da autenticação
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verificando autenticação...</p>
    </div>
    </div>
    );
  }

  // Redirecionar se não estiver autenticado
  if (!isAuthenticated) {
    navigate("/login");
    return null;
  }

  // Removido redirecionamento de proprietários - eles também podem ver favoritos

  const handleRemoveFromFavoritos = async (propertyId: number) => {
    const success = await removeFromFavoritos(propertyId);
    if (success) {
      toast({
        title: "Removido dos favoritos",
        description: "Propriedade removida dos seus favoritos.",
      });
    }
  };

  const handleClearFavoritos = async () => {
    const success = await clearFavoritos();
    if (success) {
      toast({
        title: "Favoritos limpos",
        description: "Todos os favoritos foram removidos.",
      });
    }
  };

  return (
    <MinhaAreaPageShell className="bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Meus Favoritos
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Propriedades que você salvou para mais tarde
            </p>
    </div>
          
          {favoritos.length > 0 && (
            <Button
              variant="outline"
              onClick={handleClearFavoritos}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Limpar Todos
            </Button>
          )}
    </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Carregando favoritos...</p>
    </div>
    </div>
        ) : favoritos.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <Heart className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Nenhum favorito ainda
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Comece explorando propriedades e adicione suas favoritas aqui.
            </p>
            <Button onClick={() => navigate("/")}>
              Explorar Propriedades
            </Button>
    </div>
        ) : (
          /* Favoritos Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favoritos.map((item) => (
              <Card
                key={item.id}
                className="overflow-hidden hover:shadow-xl transition-all cursor-pointer group"
              >
                <div
                  onClick={() => navigate(`/imovel/${item.id}`)}
                  className="relative h-56 overflow-hidden"
                >
                  <img
                    src={getCoverImageUrl(item)}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-lg mb-3 text-foreground line-clamp-1">
                    {item.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-muted-foreground mb-3">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm line-clamp-1">{item.location}</span>
    </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{item.max_guests} hóspedes</span>
    </div>
                    <div className="flex items-center gap-1">
                      <Bed className="h-4 w-4" />
                      <span>{item.bedrooms} quartos</span>
    </div>
    </div>

                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Preços variam por período
                      </p>
                      <span className="text-xs text-muted-foreground">Consulte disponibilidade</span>
    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFromFavoritos(item.id);
                      }}
                      className="hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
    </div>
                </CardContent>
              </Card>
            ))}
    </div>
        )}
    </MinhaAreaPageShell>
  );
};

export default Favoritos;
